import React, {useMemo, useState} from "react";
import {useWatch} from "react-hook-form";
import {FormComponentProps, FormType, QCChecklistComponent} from "../../DynamicFormTypes.ts";
import {useFormMethods, useFormStatic} from "../../DyanmicFormContext.tsx";
import {TypedRule} from "../../../../api/ApiTypes.ts";

interface Override {
    ruleId: string;
    reason: string;
    type: FormType;
}

type MapOfRulesByType = Map<FormType, TypedRule[]>;

const RuleRow: React.FC<{ rule: TypedRule; overrides: Override[], saveOverride: (rule: TypedRule, reason: string) => void, removeOverride: (rule: TypedRule) => void }> = ({ rule, overrides, saveOverride, removeOverride }) => {
    const [ruleIsInEditMode, setRuleIsInEditMode] = useState(false);
    const existingOverride = overrides.find(o => o.ruleId.toLowerCase() === rule.ruleId.toLowerCase());
    const ruleIsChecked = !!existingOverride;
    const [pendingOverrideReason, setPendingOverrideReason] = useState(existingOverride?.reason ?? "");
    const canSave = pendingOverrideReason.trim() !== "" && pendingOverrideReason !== existingOverride?.reason;



    return (
        <div
            className={`flex flex-col gap-2 px-3 py-2 text-left rounded-md border border-slate-400 cursor-pointer ${ruleIsChecked ? "bg-slate-200" : ""}`}
            onClick={(e) => {
                setRuleIsInEditMode(!ruleIsInEditMode);
                e.stopPropagation();
            }}
        >
            <div className="min-w-0 flex flex-col text-sm">
                <span className="truncate">{rule.ruleId}</span>
                <span className="text-[10px] leading-tight tracking-tight opacity-60 truncate">
                    {rule.ruleDescription}
                </span>
            </div>

            {ruleIsInEditMode && (
                <div className="flex flex-row gap-2">
                    <textarea
                        className="w-full rounded-md border p-2 text-sm"
                        rows={3}
                        placeholder="Input the reason for overriding this rule"
                        value={pendingOverrideReason}
                        onChange={e => setPendingOverrideReason(e.target.value)}
                        onClick={e => e.stopPropagation()}
                    />
                    <button
                        type="button"
                        className={`rounded-md border px-3 py-1 text-sm ${!canSave ? "opacity-50 cursor-not-allowed" : ""}`}
                        disabled={!canSave}
                        onClick={e => {
                            e.stopPropagation();
                            setRuleIsInEditMode(false);
                            saveOverride(rule, pendingOverrideReason);
                        }}
                    >
                        Save Override
                    </button>
                    {ruleIsChecked && (
                        <button
                            type="button"
                            className="rounded-md border px-3 py-1 text-sm"
                            onClick={e => {
                                e.stopPropagation();
                                setPendingOverrideReason("");
                                setRuleIsInEditMode(false);
                                removeOverride(rule);
                            }}
                        >
                            Remove Override
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const TypeGroup: React.FC<{ ruleType: FormType; listOfSameType: TypedRule[]; overrides: Override[], saveOverride: (rule: TypedRule, reason: string) => void, removeOverride: (rule: TypedRule) => void }> = ({ ruleType, listOfSameType, overrides, saveOverride, removeOverride }) => {
    const [typeIsOpen, setTypeIsOpen] = useState(false);
    const countOfTypeOverridden = listOfSameType.filter(rule => overrides.some(override => matches(override, rule))).length;

    return (
        <div className="rounded-md border w-full flex items-start px-3 py-2 text-left"
             onClick={() => setTypeIsOpen(!typeIsOpen)}
        >
            <div className="flex flex-col w-full">
                <div className="flex flex-row w-full">
                    <div className="flex flex-col w-full">
                        <div className="text-sm font-semibold">{ruleType}</div>
                        <div className="text-xs opacity-70">{listOfSameType.length} total</div>
                        <div className="text-xs opacity-70">{countOfTypeOverridden} overridden</div>
                    </div>
                    <div className="text-sm opacity-70 text-right">{typeIsOpen ? "Collapse" : "Expand"}</div>
                </div>
                {typeIsOpen && (
                    <div className="flex flex-col gap-2 p-3">
                        {listOfSameType.map(rule => (
                            <RuleRow key={rule.ruleId} rule={rule} overrides={overrides} saveOverride={saveOverride} removeOverride={removeOverride} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const matches = (override: Override, rule: TypedRule) => override.ruleId === rule.ruleId;

const QCChecklistRenderer: React.FC<FormComponentProps<QCChecklistComponent>> = ({ component }) => {
    const { formMethods } = useFormMethods();
    const { customValidator } = useFormStatic();

    const overrides = (useWatch({
        control: formMethods.control,
        name: component.fieldKey,
    }) as Override[]) || [];

    const removeOverride = (rule: TypedRule) => {
        formMethods.setValue(component.fieldKey, overrides.filter(override => !matches(override, rule)));
    };

    const saveOverride = (rule: TypedRule, reason: string) => {
        if (!reason) return;

        const next: Override[] = [
            ...overrides.filter(override => !matches(override, rule)),
            { ruleId: rule.ruleId, reason: reason, type: rule.type },
        ];

        formMethods.setValue(component.fieldKey, next);
    };

    const groupedByType = useMemo<MapOfRulesByType>(() => {
        const map: MapOfRulesByType = new Map();

        for (const trigger of customValidator?.getTriggers() ?? []) {
            for (const rule of trigger.typedRules) {
                const ruleType = map.get(rule.type) ?? [];
                if (!ruleType.some(r => r.ruleId === rule.ruleId)) {
                    ruleType.push(rule);
                }
                map.set(rule.type, ruleType);
            }
        }

        return map;
    }, [customValidator]);

    return (
        <div className="flex flex-col gap-3 w-full">
            {groupedByType.size === 0 ? (
                <div className="text-sm opacity-70">No QC rules were returned from custom triggers.</div>
            ) : (
                <div className="flex flex-col gap-3">
                    {[...groupedByType.entries()].map(([ruleType, listOfSameType]) => (
                        <TypeGroup
                            key={ruleType}
                            ruleType={ruleType}
                            listOfSameType={listOfSameType}
                            overrides={overrides}
                            saveOverride={saveOverride}
                            removeOverride={removeOverride}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export const QCChecklist = React.memo(QCChecklistRenderer);